import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, ShareNetwork, Factory, ClipboardText, Kanban, ChatCircleDots, Storefront } from '@phosphor-icons/react';
import { canUseApp } from '@/auth/AuthContext';
import { useChatUnread } from '@/hooks/useChatUnread';
import { useT } from '@/i18n/LanguageContext';

const APPS = [
  { app: 'kintai', path: '/forms', prefixes: ['/forms', '/history', '/submission'], Icon: FileText, key: 'tab.kintai' },
  { app: 'mfg', path: '/mfg', prefixes: ['/mfg'], Icon: Factory, key: 'tab.mfg' },
  { app: 'shizai', path: '/shizai', prefixes: ['/shizai'], Icon: ClipboardText, key: 'tab.shizai' },
  { app: 'marketing', path: '/marketing', prefixes: ['/marketing'], Icon: Storefront, key: 'tab.marketing' },
  { app: 'planner', path: '/planner', prefixes: ['/planner'], Icon: Kanban, key: 'tab.planner' },
  { app: 'it', path: '/it', prefixes: ['/it'], Icon: ShareNetwork, key: 'tab.it' },
  { app: 'chat', path: '/chat', prefixes: ['/chat'], Icon: ChatCircleDots, key: 'tab.chat' },
];

/** Phone-only fixed bottom bar for one-thumb switching between apps.
 *  Auto-hides while scrolling down (more room for long tables), reveals on scroll up. */
export default function BottomTabBar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const chatUnread = useChatUnread(user);

  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY.current;
      if (y < 64) setHidden(false);              // always show near the top
      else if (dy > 6) setHidden(true);          // scrolling down → hide
      else if (dy < -6) setHidden(false);        // scrolling up → reveal
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const items = APPS.filter((a) => canUseApp(user, a.app));
  if (items.length < 2) return null;

  // Hide the global bottom tab bar on scanner-focused pages so the scanner
  // viewport stays the hero element (per Feb 2026 mobile scanner redesign).
  // Users still reach other apps via the top-right hamburger menu.
  const p = location.pathname;
  if (p === '/scanner' || p.startsWith('/scanner/')) return null;

  const isActive = (a) => a.prefixes.some((pre) => p === pre || p.startsWith(pre + '/') || p.startsWith(pre));

  return (
    <nav
      data-testid="bottom-tab-bar"
      className={`md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-zinc-200 no-print transition-transform duration-300 ease-out shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.18)] ${hidden ? 'translate-y-full' : 'translate-y-0'}`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch">
        {items.map((a) => {
          const active = isActive(a);
          return (
            <button
              key={a.app}
              type="button"
              onClick={() => navigate(a.path)}
              data-testid={`bottom-tab-${a.app}`}
              aria-current={active ? 'page' : undefined}
              className={`relative flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2 transition-all active:scale-90 ${active ? 'text-teal-700' : 'text-zinc-500 active:bg-zinc-50'}`}
            >
              {active && <span className="absolute top-0 h-0.5 w-7 rounded-b-full bg-teal-700" />}
              <span className="relative">
                <a.Icon size={22} weight={active ? 'fill' : 'regular'} />
                {a.app === 'chat' && chatUnread > 0 && (
                  <span data-testid="bottom-tab-chat-badge" className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                    {chatUnread > 99 ? '99+' : chatUnread}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-medium leading-none truncate max-w-full px-0.5">{t(a.key)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
